<script>
export default {
  name: "DidoMillesimeTable",
};
</script>

<script setup>
import { ref, computed, watch, watchEffect } from "vue";
import api from "../../../api";
import {
  showNotification,
  copyLinkToClipboard,
  apiValueToLocaleString,
} from "../../../utils";
import DidoMillesimeTableRowColumnsFilters from "./rows/ColumnsFilters.vue";
import DidoMillesimeTableRowExtendedFilters from "./rows/ExtendedFilters.vue";
import DidoMillesimeTableRowContainsOrValuesListFilters from "./rows/BasicFilters.vue";
import DidoMillesimeTablePagination from "./Pagination.vue";
import DidoMillesimeTableModalDownload from "./modals/Download.vue";

// define Props
const props = defineProps({
  apiUrl: { type: String, required: true },
  rid: { type: String, required: true },
  millesime: { type: String, required: true },
  infos: { type: Object, required: true },
  initialFilter: { type: Object, required: false, default: null },
});

// define ref for root template element
const tableWrapperEl = ref(null);

// define reactive data
const pageSizeOptions = ref(api.rowPageSizeOptions);
const columns = ref(
  props.infos.columns.map(({ name, description, type, precision }) => ({
    name,
    label: name,
    field: name,
    required: false,
    align: "center",
    sortable: true,
    description,
    type,
    precision,
  })),
);
const showFilters = ref("simple");
const loadingRows = ref(false);
const loadingCount = ref(false);
const selectedColumns = ref([]);
const columnsFilter = ref(
  props.infos.columns.reduce((filters, column) => {
    const newFilters = { ...filters };
    newFilters[column.name] = { type: "", value: "" };
    return newFilters;
  }, {}),
);
const groupsFilter = ref(
  props.infos.extendedFilters.reduce((filters, group) => {
    const newFilters = { ...filters };
    newFilters[group.name] = { type: "", value: "" };
    return newFilters;
  }, {}),
);
const rows = ref([]);
const serverProps = ref({ page: 1, rowsPerPage: "10", rowsNumber: null });
const lastApiCallParams = ref(null);
const dialogLinksOpened = ref(false);

// define computed data
const optionsColumnsSelection = computed(() => [
  { value: "uncheck_all", label: "Décocher tout" },
  { value: "check_all", label: "Cocher tout" },
  ...props.infos.columns.map((c) => ({
    value: c.name,
    label: c.name,
    description: c.description,
  })),
]);

const visibleColumns = computed(() =>
  selectedColumns.value.length
    ? selectedColumns.value
    : props.infos.columns.map((c) => c.name),
);

const hasContainsFilter = computed(() => {
  const columnsWithContainsFilter = props.infos.columns
    .filter((column) => column.filters.find((filter) => filter === "contains"))
    .map((column) => column.name);
  return (
    visibleColumns.value.filter((name) =>
      columnsWithContainsFilter.includes(name),
    ).length > 0
  );
});

const hasValuesListFilter = computed(() => {
  const columnsWithValuesListFilter = props.infos.columns
    .filter((column) => column.availableValues && column.availableValues.length)
    .map((column) => column.name);
  return (
    visibleColumns.value.filter((name) =>
      columnsWithValuesListFilter.includes(name),
    ).length > 0
  );
});

const textSwitchFilters = computed(() => {
  if (showFilters.value === "advanced")
    return {
      label: "Désactiver les filtres avancés",
      title:
        "Désactiver les filtres avancés pour sélectionner les enregistrements dans le tableau",
    };
  return {
    label: "Utiliser les filtres avancés",
    title:
      "Utiliser les filtres avancés pour sélectionner les enregistrements dans le tableau",
  };
});

const showRowColumnsFilters = computed(() => showFilters.value === "advanced");
const showRowExtendedFilters = computed(
  () =>
    showFilters.value === "advanced" && props.infos.extendedFilters.length > 0,
);
const showRowContainsOrValuesListFilters = computed(
  () =>
    showFilters.value === "simple" &&
    (hasContainsFilter.value || hasValuesListFilter.value),
);

const activeFilters = computed(() => {
  const filters = {};
  Object.entries(columnsFilter.value).forEach(([colName, filter]) => {
    if (filter.type && filter.value) {
      const colFilter = { type: filter.type };
      if (["in", "nin"].includes(filter.type)) {
        colFilter.value = filter.value.split("$SEPARATOR$").join(",");
      } else {
        colFilter.value = filter.value;
      }
      filters[colName] = colFilter;
    }
  });
  Object.entries(groupsFilter.value).forEach(([groupName, filter]) => {
    if (filter.type && filter.value)
      filters[groupName] = { type: filter.type, value: filter.value };
  });
  return filters;
});

const countRows = async (filters) => {
  try {
    // api call
    loadingCount.value = true;
    const result = await api.countRows(
      props.apiUrl,
      props.rid,
      props.millesime,
      filters,
    );

    // update ref
    serverProps.value.rowsNumber = result;
  } catch (error) {
    serverProps.value.rowsNumber = null;
  }
  loadingCount.value = false;
};

const getPage =
  (filters, columns) =>
  async ({ pagination: { page, rowsPerPage, sortBy, descending } }) => {
    try {
      // retrieve api params
      const apiParams = {
        page,
        pageSize: rowsPerPage,
        sort: sortBy ? (descending ? "-" : "") + sortBy : null,
        filters,
        columns,
      };

      // do api call
      loadingRows.value = true;
      const result = await api.getPage(
        props.apiUrl,
        props.rid,
        props.millesime,
        apiParams,
      );

      // if no pagination result throw error
      if (!result) throw new Error();

      // update ref of table components
      serverProps.value.page = page;
      serverProps.value.rowsPerPage = rowsPerPage;
      serverProps.value.sortBy = sortBy;
      serverProps.value.descending = descending;

      // update ref with rows
      rows.value = result;

      // update ref with params of las api call
      lastApiCallParams.value = { ...apiParams };
    } catch (error) {
      rows.value = [];
      showNotification({
        color: "negative",
        icon: "mdi-alert-decagram",
        message: error.message,
        position: "top",
        timeout: 2500,
      });
    }
    loadingRows.value = false;
  };

const deleteFiltersOfUnselectedColumns = () => {
  props.infos.columns
    .filter(({ name }) => !selectedColumns.value.includes(name))
    .forEach(({ name }) => {
      if (columnsFilter.value[name].type) columnsFilter.value[name].type = "";
      if (columnsFilter.value[name].value) columnsFilter.value[name].value = "";
    });
};

// watching initialFilter props
watchEffect(() => {
  if (props.initialFilter) {
    if (props.initialFilter.orderBy) {
      const descending = props.initialFilter.orderBy.substring(0, 1) === "-";
      const sortField = descending
        ? props.initialFilter.orderBy.substring(1)
        : props.initialFilter.orderBy;
      if (columns.value.find(({ name }) => name === sortField)) {
        serverProps.value.sortBy = sortField;
        serverProps.value.descending = descending;
      }
    }
    if (props.initialFilter.columns) {
      const columnsAsked = props.initialFilter.columns.split(",");
      selectedColumns.value = columnsAsked.filter((col) =>
        columns.value.find(({ name }) => name === col),
      );
    }

    props.infos.columns.forEach(({ name, filters }) => {
      // a filter clause exists for the column in initial filter
      if (props.initialFilter[name]) {
        // check if column of filter is in selected columns
        if (selectedColumns.value.find((colName) => colName === name)) {
          // check is the initial filter type for the coumn is in available filters of column
          if (
            filters.find(
              (filterName) => filterName === props.initialFilter[name].type,
            )
          ) {
            showFilters.value = "advanced";
            columnsFilter.value[name] = props.initialFilter[name];
          }
        }
      }
    });
    props.infos.extendedFilters.forEach(({ name, filters, columns }) => {
      // a filter clause exists for the extended group in initial filter
      if (props.initialFilter[name]) {
        // check if at least one column of extended group is in selected columns
        if (
          columns.filter((val) => selectedColumns.value.includes(val)).length
        ) {
          // check is the initial filter type for the group is in available filters of group
          if (
            filters.find(
              (filterName) => filterName === props.initialFilter[name].type,
            )
          ) {
            showFilters.value = "advanced";
            groupsFilter.value[name] = props.initialFilter[name];
          }
        }
      }
    });
  }
});

// watching when selected columns change
watch(
  () => selectedColumns.value,
  () => deleteFiltersOfUnselectedColumns(),
);

// watching activeFilters and selectedColumns
watch(
  () => [selectedColumns.value, activeFilters.value],
  () => {
    if (!lastApiCallParams.value) {
      countRows(activeFilters.value);
      getPage(
        activeFilters.value,
        selectedColumns.value,
      )({ pagination: serverProps.value });
      return;
    }

    if (
      JSON.stringify(lastApiCallParams.value.filters) !==
      JSON.stringify(activeFilters.value)
    )
      countRows(activeFilters.value);

    const currentApiCallParams = {
      page: serverProps.value.page,
      pageSize: serverProps.value.rowsPerPage,
      sort: serverProps.value.sortBy
        ? (serverProps.value.descending ? "-" : "") + serverProps.value.sortBy
        : null,
      filters: activeFilters.value,
      columns: selectedColumns.value,
    };
    if (
      JSON.stringify(lastApiCallParams.value) !==
      JSON.stringify(currentApiCallParams)
    )
      getPage(
        activeFilters.value,
        selectedColumns.value,
      )({ pagination: serverProps.value });
  },
  { immediate: true },
);

const rowKey = (row) => {
  const sort = serverProps.value.sortBy
    ? [
        {
          field: serverProps.value.sortBy,
          order: serverProps.value.descending ? "desc" : "asc",
        },
      ]
    : [];
  const filters = Object.entries(activeFilters.value).map(([key, filter]) => ({
    field: key,
    name: filter.type,
    value: filter.value,
  }));

  const spec = {
    format: "rows",
    page: serverProps.value.page,
    pageSize: serverProps.value.pageSize,
    sort,
    filters,
    columns: selectedColumns.value,
  };
  return `${api.getDownloadUrl(
    props.apiUrl,
    props.rid,
    props.millesime,
    spec,
  )}-${row.index}`;
};

const switchFilters = () => {
  // switch
  showFilters.value = showFilters.value === "advanced" ? "simple" : "advanced";
  // reset filters
  columnsFilter.value = props.infos.columns.reduce((filters, column) => {
    const newFilters = { ...filters };
    newFilters[column.name] = { type: "", value: "" };
    return newFilters;
  }, {});
  groupsFilter.value = props.infos.extendedFilters.reduce((filters, group) => {
    const newFilters = { ...filters };
    newFilters[group.name] = { type: "", value: "" };
    return newFilters;
  }, {});
};

const onColumnSelection = (colNames) => {
  if (colNames.includes("check_all"))
    selectedColumns.value = props.infos.columns.map((c) => c.name);
  if (colNames.includes("uncheck_all")) selectedColumns.value = [];
  if (!colNames.includes("check_all") && !colNames.includes("uncheck_all")) {
    // reorder selectedColumns
    const ordered = [];
    props.infos.columns.forEach((col) => {
      if (colNames.includes(col.name)) ordered.push(col.name);
    });
    selectedColumns.value = ordered;
  }
};

// define computed object for download links
const downloadLinks = computed(() => {
  // initialize return object
  const linksObject = {};

  // create sort clause
  const sort = serverProps.value.sortBy
    ? [
        {
          field: serverProps.value.sortBy,
          order: serverProps.value.descending ? "desc" : "asc",
        },
      ]
    : [];
  // create filter clause
  const filters = Object.entries(activeFilters.value).map(([key, filter]) => ({
    field: key,
    name: filter.type,
    value: filter.value,
  }));
  // create columns clause
  const columns = selectedColumns.value;

  linksObject.csv = api.getDownloadUrl(
    props.apiUrl,
    props.rid,
    props.millesime,
    {
      format: "csv",
      headers: {
        withColumnName: true,
        withColumnDescription: true,
        withColumnUnit: false,
      },
      sort,
      filters,
      columns,
    },
  );
  linksObject.xlsx = api.getDownloadUrl(
    props.apiUrl,
    props.rid,
    props.millesime,
    {
      format: "xlsx",
      headers: {
        withColumnName: true,
        withColumnDescription: true,
        withColumnUnit: false,
      },
      sort,
      filters,
      columns,
    },
  );
  linksObject.json = api.getDownloadUrl(
    props.apiUrl,
    props.rid,
    props.millesime,
    { format: "json", sort, filters, columns },
  );

  linksObject.geojson = {};
  props.infos.geoFields.forEach((geoField) => {
    linksObject.geojson[geoField] = api.getDownloadUrl(
      props.apiUrl,
      props.rid,
      props.millesime,
      { format: "spatial/geojson", geoField, sort, filters, columns },
    );
  });

  linksObject.rows = api.getDownloadUrl(
    props.apiUrl,
    props.rid,
    props.millesime,
    {
      format: "rows",
      page: serverProps.value.page,
      pageSize: serverProps.value.rowsPerPage,
      sort,
      filters,
      columns,
    },
  );

  return linksObject;
});
</script>

<template>
  <div
    v-if="props.infos.columns"
    ref="tableWrapperEl"
    class="dido-millesime-tab-table"
  >
    <QTable
      v-model:pagination="serverProps"
      class="dido-millesime-table"
      color="secondary"
      dense
      flat
      square
      :rows-per-page-options="pageSizeOptions"
      :loading="loadingRows"
      :rows="rows"
      :columns="columns"
      :visible-columns="visibleColumns"
      :row-key="(row) => rowKey(row)"
      separator="cell"
      @request="
        (tableArgs) => getPage(activeFilters, selectedColumns)(tableArgs)
      "
    >
      <template #top>
        <div class="row-all-center q-col-gutter-sm q-mb-sm">
          <span>
            <QBtn
              size="sm"
              outline
              color="secondary"
              square
              dense
              :label="textSwitchFilters.label"
              :title="textSwitchFilters.title"
              @click="switchFilters"
            />
          </span>
          <span>
            <QSelect
              v-model="selectedColumns"
              dense
              options-dense
              square
              outlined
              multiple
              filter
              hide-underline
              hide-bottom-space
              hide-dropdown-icon
              align="center"
              color="secondary"
              label-color="secondary"
              title="Sélectionner les colonnes à afficher dans le tableau"
              :options="optionsColumnsSelection"
              emit-value
              @update:model-value="onColumnSelection"
            >
              <template #selected>
                <span class="text-secondary text-weight-medium text-uppercase">
                  Colonnes à afficher (<span>{{
                    selectedColumns.length === 0
                      ? "toutes"
                      : selectedColumns.length
                  }}</span
                  >)
                </span>
              </template>
              <template #option="{ itemProps, opt, selected, toggleOption }">
                <QItem
                  v-bind="itemProps"
                  class="content-center justify-center items-center"
                >
                  <QItemSection>
                    <QItemLabel class="text-small">{{ opt.label }}</QItemLabel>
                    <QTooltip
                      v-if="opt.description"
                      :delay="550"
                      class="border-secondary text-secondary text-caption bg-white no-border-radius"
                    >
                      {{ opt.description }}
                    </QTooltip>
                  </QItemSection>
                  <QItemSection side style="height: 15px">
                    <QToggle
                      :model-value="selected"
                      color="secondary"
                      dense
                      size="xs"
                      @update:model-value="toggleOption(opt)"
                    />
                  </QItemSection>
                </QItem>
              </template>
            </QSelect>
          </span>
          <span v-if="downloadLinks">
            <QBtn
              size="sm"
              outline
              color="secondary"
              square
              dense
              label="télécharger le résultat du filtre"
              title="Télécharger le résultat du filter dans différents formats"
              @click="dialogLinksOpened = true"
            />
          </span>
          <span v-if="downloadLinks">
            <QBtn
              size="sm"
              outline
              color="secondary"
              square
              dense
              icon="mdi-clipboard-outline"
              title="Copier le lien correspondant à cette page du tableau dans le presse papier"
              @click="copyLinkToClipboard(downloadLinks.rows, tableWrapperEl)"
            />
          </span>
        </div>
      </template>
      <template #header="headerProps">
        <QTr :props="headerProps">
          <QTh
            v-for="col in headerProps.cols"
            :key="col.name"
            :props="headerProps"
            class="q-pa-xs bg-secondary text-white text-bold"
          >
            <span>
              # {{ col.label }}
              <QTooltip
                class="border-secondary text-secondary text-caption bg-white no-border-radius"
                anchor="center middle"
                self="top middle"
              >
                <div>{{ col.description }}</div>
                <br />
                <div>
                  Type
                  <a
                    href="https://specs.frictionlessdata.io/table-schema/"
                    title="Spécifications Table Schema"
                    >Table Schema</a
                  >
                  : {{ col.type }}
                  <span v-if="col.format">({{ col.format }})</span>
                </div>
              </QTooltip>
            </span>
          </QTh>
        </QTr>
      </template>
      <template #top-row>
        <DidoMillesimeTableRowColumnsFilters
          v-model="columnsFilter"
          :show="showRowColumnsFilters"
          :visible-columns="visibleColumns"
          :available-filters-in-columns="props.infos.columns"
          :api-url="props.apiUrl"
        />
        <DidoMillesimeTableRowExtendedFilters
          v-model="groupsFilter"
          :show="showRowExtendedFilters"
          :visible-columns="visibleColumns"
          :available-filters-in-groups="props.infos.extendedFilters"
          :api-url="props.apiUrl"
        />
        <DidoMillesimeTableRowContainsOrValuesListFilters
          v-model="columnsFilter"
          :show="showRowContainsOrValuesListFilters"
          :visible-columns="visibleColumns"
          :available-filters-in-columns="props.infos.columns"
          :api-url="props.apiUrl"
        />
      </template>
      <template #body-cell="bodyCellProps">
        <QTd
          :class="[
            'dido-millesime-table-cell',
            ['number', 'integer'].includes(bodyCellProps.col.type)
              ? 'text-right'
              : '',
          ]"
        >
          {{
            apiValueToLocaleString(
              bodyCellProps.value,
              bodyCellProps.col.type,
              bodyCellProps.col.precision,
            )
          }}
        </QTd>
      </template>
      <template #loading>
        <QInnerLoading showing color="secondary" />
      </template>
      <template #pagination="paginationInfos">
        <DidoMillesimeTablePagination
          :pagination-infos="paginationInfos"
          :loading="loadingCount"
        />
      </template>
    </QTable>
    <DidoMillesimeTableModalDownload
      v-if="downloadLinks"
      v-model="dialogLinksOpened"
      :table-wrapper-el="tableWrapperEl || {}"
      :server-props="serverProps || {}"
      :last-api-call-params="lastApiCallParams || {}"
      :api-url="props.apiUrl"
      :rid="props.rid"
      :millesime="props.millesime"
      :infos="props.infos"
      :download-links="downloadLinks"
    />
  </div>
</template>

<style lang="sass" scoped>
.dido-millesime-tab-table
  // Remove padding of top row with buttons
  :deep( .q-table--dense .q-table__top)
    padding: 0
  // Adjust height of QSelect in top row
  :deep( .q-table__top .q-select.q-field--auto-height.q-field--dense .q-field__control)
    height: auto
    min-height: auto
    max-height: none
  :deep( .q-table__top .q-select.q-field--auto-height.q-field--dense .q-field__native)
    height: auto
    min-height: auto
    max-height: none
  // Adjust padding of QSelect in top row
  :deep( .q-table__top .q-select .q-field__native)
    padding: 0.285em
  :deep( .q-table__top .q-select.q-field--outlined .q-field__control)
    padding: 0
  // Adjust font specs of QSelect
  :deep( .q-table__top .q-select .q-btn)
    font-size: 10px
    font-weight: 500
  :deep( .q-table__top .q-select.q-field)
    font-size: 10px
  // Adjust line-height of QSelect
  :deep( .q-table__top .q-select.q-field--auto-height .q-field__native)
    line-height: 1.715em
  // Set color border of QSelect in top row
  :deep( .q-table__top .q-select.q-field--outlined .q-field__control:before)
    border: 1px solid var(--q-secondary)
.dido-millesime-table-cell
  font-size: 0.8em !important
</style>
